// Accounts (docs/APP_REFERENCE.md "Accounts"): signing in with Google keeps the city passport, the settings and the
// chat name on every device, through the live worker's /account (worker/account.js). The session is an HttpOnly
// cookie for that host, so the page only knows it's signed in by asking (GET /account) and keeps a marker with the
// email to show and the account's id (`lofi-cities:account`) until a sync says otherwise. Nothing runs for a visitor
// who never signs in.
//
// A sync sends what this device added: the passport's seconds beyond what it handed to this account before
// (`lofi-cities:passport-sent`, { account, s, pending }; for another account, or one deleted and made again, that's
// all of it), as one batch with an id that's sent again until it's answered, so it's added once; every synced setting with when it last changed here (`lofi-cities:settings-at`; 0 for a setting
// from before, which any change elsewhere beats); and the live id, the chat identity, for an account that has none.
// The answer is the account: its passport totals plus what this device listened meanwhile become the passport here,
// its newer settings are applied, its live id becomes this device's. So the passport here never shrinks. Signing out
// keeps the device's copy: signing in again adds only what came after, another account takes all of it.
// The account may also pick its own chat name instead of the random one (setName; the worker keeps it with the chat,
// worker/chat.js), which signing in reports (`name` in onChange's state) for the page to offer one.
// Pure but for the clocks, storage, fetch and locks it's handed, so node can test it: node --test tests/

const SITE = 'https://live.loficities.com';
const DEV = 'http://localhost:8787';
export const SYNC_MS = 2 * 60000;        // while signed in and listening: the passport's new seconds go this often
export const SOON_MS = 3000;             // a changed setting goes this soon (changes in a row go as one)
export const STALE_MS = 60000;           // a tab coming back into view after this long asks for other devices' changes
export const LOOK_MS = 2500;             // while a sign-in is open: look for the session this often,
export const LOOK_FOR_MS = 5 * 60000;    // for this long
export const WAIT_MS = 10000;            // a request this slow is given up (the next sync tries again)

/** The worker's /account base for this page, or null where there's no live worker (OBS, deploy previews, tools). */
export function accountUrl(loc, obs) {
  if (obs) return null;
  if (loc.hostname === 'loficities.com') return SITE;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(loc.hostname);
  return local && new URLSearchParams(loc.search).has('live') ? DEV : null;
}

const own = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const secs = o => (o && typeof o === 'object' && !Array.isArray(o) ? o : {});

/** What `local` has over `sent`, in whole seconds per city ({} when nothing). */
export function passportDelta(local, sent) {
  const add = {}, s = secs(sent);
  for (const [city, v] of Object.entries(secs(local))) {
    const d = Math.floor(v) - Math.floor(Number.isFinite(s[city]) ? s[city] : 0);
    if (city !== '__proto__' && Number.isFinite(d) && d >= 1) add[city] = d;
  }
  return add;
}

/**
 * After a sync: the account's totals `server` plus what the device listened since it sent (`local` now over
 * `sent`), the passport to keep here; and the account's totals, the new `sent`.
 */
export function afterSync(local, sent, server) {
  const s = {}, since = passportDelta(local, sent), totals = {};
  for (const [city, v] of Object.entries(secs(server))) if (city !== '__proto__' && Number.isFinite(v) && v >= 1) totals[city] = Math.floor(v);
  for (const city of new Set([...Object.keys(totals), ...Object.keys(since)])) s[city] = (totals[city] ?? 0) + (since[city] ?? 0);
  return { s, sent: totals };
}

/**
 * The settings to apply from an answer, in its order: newer than this device's, or unknown here. A setting this
 * device has never timed (from before accounts) loses to the account's even at 0, so devices end up agreeing.
 */
export function newerSettings(server, ats, has) {
  return Object.entries(secs(server)).filter(([key, e]) => e && typeof e === 'object' && Number.isFinite(e.at)
    && (!has(key) || e.at > (Number.isFinite(ats[key]) ? ats[key] : -1)));
}

const randomId = () => (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

/**
 * The account, for a page whose worker is at `base`. `store` is the page's ({ get, set } under its prefix);
 * `passport` reads and writes the stored passport ({ flush, load, save, reload }: flush counts this tab's listening
 * in first, reload has its counter read what a sync left); `settings` is the synced keys ({ keys, apply(key, value)
 * → false to try again later }); `liveId()` this device's chat identity, `onLiveId(id)` the account's taking its
 * place; `onChange(state)` hears signing in and out; `onEvent(name, props)` the analytics. `fetcher`, `locks`,
 * `open` and `now` are the browser's (tests swap them).
 */
export function createAccount({
  base, store, passport, settings, liveId, onLiveId = () => {}, onChange = () => {}, onEvent = () => {},
  fetcher = (...a) => fetch(...a), locks = globalThis.navigator?.locks, open = (...a) => window.open(...a),
  now = () => Date.now(), timers = globalThis, nav = url => { location.href = url; }, here = () => location.href,
}) {
  const marker = store.get('account', null);
  let state = marker && typeof marker === 'object' && 'email' in marker ? { email: marker.email, account: marker.account ?? null } : null;   // signed in
  let soon = 0, looking = null, syncedAt = 0, applying = false, failed = false, dirty = false, tail = Promise.resolve(), waiting = null;
  const keys = new Set(settings.keys);
  const ats = () => secs(store.get('settings-at', {}));
  /** What this device handed to the signed-in account: nothing yet if it last handed it to another. */
  const sentState = () => {
    const p = store.get('passport-sent', null), account = state?.account ?? null;
    if (!account || p?.account !== account) return { account, s: {}, pending: null };
    return { account, s: secs(p.s), pending: p.pending && typeof p.pending.id === 'string' ? { id: p.pending.id, add: secs(p.pending.add) } : null };
  };

  const call = (path, body, { keepalive = false } = {}) => fetcher(`${base}${path}`, {
    method: body === undefined ? 'GET' : 'POST', credentials: 'include', keepalive, signal: AbortSignal.timeout?.(WAIT_MS),
    ...(body !== undefined && { body: typeof body === 'string' ? body : JSON.stringify(body), headers: { 'Content-Type': 'text/plain;charset=UTF-8' } }),
  });
  function setState(next, how, more = {}) {
    const was = state;
    state = next;
    store.set('account', next);
    if (!!was !== !!next || was?.email !== next?.email) onChange(next && { ...next, ...more }, how);
  }
  function signedOut(how) {
    if (!state) return;
    setState(null, how);
    onLiveId(randomId());                             // the account's chat name leaves with it
  }

  /** One sync, under a lock so this browser's tabs take turns (one passport, one `sent`). */
  async function syncNow({ keepalive = false } = {}) {
    if (!state) return false;
    if (!state.account && !(await who())) return false;   // which account, before anything is handed over
    passport.flush();
    const p = sentState();
    if (!p.pending) {
      const add = passportDelta(passport.load().s, p.s);
      if (Object.keys(add).length) {
        p.pending = { id: randomId(), add };
        for (const [city, v] of Object.entries(add)) p.s[city] = (p.s[city] ?? 0) + v;
        store.set('passport-sent', p);
      }
    }
    const at = ats(), sending = {};
    dirty = false;
    for (const key of keys) {
      const v = store.get(key, undefined);
      if (v !== undefined) sending[key] = { v, at: at[key] ?? 0 };
    }
    let res;
    try {
      res = await call('/account/sync', { ...(p.pending && { batch: p.pending.id, add: p.pending.add }), settings: sending, liveId: liveId() }, { keepalive });
    } catch {
      dirty = true;
      fail('network');
      return false;
    }
    if (res.status === 401) { signedOut('expired'); return false; }
    if (!res.ok) { dirty = true; fail(String(res.status)); return false; }
    let a;
    try { a = await res.json(); } catch { fail('json'); return false; }
    if (!state || a.account !== state.account) return false;   // signed out, or into another account, meanwhile
    syncedAt = now();
    passport.flush();                                 // this tab's seconds while it waited, counted in before it's replaced
    const local = passport.load(), done = afterSync(local.s, sentState().s, a.passport);
    passport.save({ ...local, s: done.s });
    store.set('passport-sent', { account: a.account, s: done.sent, pending: null });
    passport.reload();
    const at2 = ats();
    for (const [key, e] of newerSettings(a.settings, at2, k => store.get(k, undefined) !== undefined)) {
      if (!keys.has(key)) continue;
      applying = true;
      let ok = true;
      try { ok = settings.apply(key, e.v) !== false; } catch (err) { console.error(err); } finally { applying = false; }
      if (ok) at2[key] = e.at;
    }
    store.set('settings-at', at2);
    if (typeof a.liveId === 'string' && a.liveId !== liveId()) onLiveId(a.liveId);
    if ((typeof a.email === 'string' || a.email === null) && a.email !== state.email) setState({ ...state, email: a.email }, 'sync');
    return true;
  }
  function fail(reason) {
    if (failed) return;
    failed = true;                                    // once a visit: the next sync tries again either way
    onEvent('account_sync_failed', { reason });
  }
  /** A sync after this tab's others; one waiting its turn answers every ask meanwhile. */
  function sync(opts) {
    timers.clearTimeout(soon);
    soon = 0;
    if (!state) return Promise.resolve(false);
    if (waiting) return waiting;
    const run = () => syncNow(opts);
    const next = tail.then(() => { waiting = null; return locks ? locks.request('lofi-cities:account', run) : run(); })
      .catch(e => { console.error(e); return false; });
    waiting = tail = next;
    return next;
  }
  /** Anything to send: the passport's new seconds, or a setting changed since the last sync. */
  const due = () => dirty || !!sentState().pending || Object.keys(passportDelta(passport.load().s, sentState().s)).length > 0;

  /**
   * The session: { email, account, name } (GET /account; `name` the chosen chat name, null for none, undefined when the
   * worker didn't say), or null when there's none or the worker can't be reached.
   */
  async function session() {
    try {
      const res = await call('/account'), a = res.ok ? await res.json() : null;
      if (!a?.signedIn || typeof a.account !== 'string') return null;
      return { email: typeof a.email === 'string' ? a.email : null, account: a.account, name: typeof a.name === 'string' || a.name === null ? a.name : undefined };
    } catch { return null; }
  }
  /** A marker without the account's id: learn it from the session. */
  async function who() {
    const s = await session();
    if (s && state) setState({ email: s.email, account: s.account }, 'sync');
    return !!s;
  }
  /** Is the session there? While a sign-in is open, every LOOK_MS (and as the page comes back into view). */
  async function look() {
    if (!looking) return;
    const s = await session();
    if (!looking) return;
    if (s) {
      const { where } = looking, { name, ...kept } = s;
      stopLooking();
      setState(kept, 'signed_in', { name });
      onEvent('signed_in', { location: where });
      sync();
    } else if (now() - looking.since > LOOK_FOR_MS) stopLooking();
  }
  function stopLooking() {
    if (looking) timers.clearInterval(looking.timer);
    looking = null;
  }

  if (typeof addEventListener === 'function') {
    addEventListener('visibilitychange', () => {
      if (!state && !looking) return;
      if (document.hidden) { if (state && due()) sync({ keepalive: true }); }
      else if (looking) look();
      else if (now() - syncedAt >= STALE_MS) sync();
    });
    addEventListener('focus', () => { if (looking) look(); });
    // another tab signed in or out: this one follows (its sync tells the worker nothing new)
    addEventListener('storage', e => {
      if (e.key !== 'lofi-cities:account') return;
      const next = store.get('account', null);
      if (!!next !== !!state) { state = next; onChange(state, 'other_tab'); }
    });
  }

  return {
    get signedIn() { return !!state; },
    get email() { return state?.email ?? null; },
    get looking() { return !!looking; },
    /** The visit begins: a page back from a same-tab sign-in, or a device signed in before, checks in. */
    start() {
      const back = store.get('account-trip', null);
      if (back) {
        store.set('account-trip', null);
        looking = { where: back.where, since: now(), timer: 0 };
        look().finally(() => { if (looking?.timer === 0) stopLooking(); });   // back without a session: cancelled
      } else if (state) sync();
      timers.setInterval(() => {
        if (!state) return;
        const add = passportDelta(passport.load().s, sentState().s);
        if (Object.keys(add).length && now() - syncedAt >= SYNC_MS) sync();
      }, SYNC_MS / 4);
    },
    /** Sign in from `where` (header, settings, passport, stamp_toast): a popup, or this tab if popups are blocked. */
    signIn(where) {
      if (state) return;
      const popup = open(`${base}/account/google?mode=popup`, 'lofi-cities-sign-in', 'popup,width=480,height=640');
      onEvent('sign_in_started', { location: where, mode: popup ? 'popup' : 'page' });
      if (!popup) {
        store.set('account-trip', { where });
        nav(`${base}/account/google?mode=page&back=${encodeURIComponent(here())}`);
        return;
      }
      stopLooking();
      looking = { where, since: now(), timer: timers.setInterval(look, LOOK_MS) };
    },
    async signOut() {
      if (!state) return;
      await sync();                                   // what's not sent yet goes first
      try { await call('/account/sign-out', ''); } catch { /* the cookie stays until it expires; the page forgets it now */ }
      signedOut('signed_out');
      onEvent('signed_out', {});
    },
    /**
     * The account's chat name: `name` as typed, or null for the random one again. The worker's answer, { ok: true, name }
     * or { ok: false, why, until? } (why: site/js/live/chat.js nickRefusal's, 'words', 'taken', 'soon' until when,
     * 'hushed', or 'network', 'signed_out', 'unavailable' when it couldn't be asked).
     */
    async setName(name) {
      if (!state) return { ok: false, why: 'signed_out' };
      let res;
      try { res = await call('/account/nickname', { name, liveId: liveId() }); } catch { return { ok: false, why: 'network' }; }
      if (res.status === 401) { signedOut('expired'); return { ok: false, why: 'signed_out' }; }
      let a = null;
      try { a = await res.json(); } catch { /* below */ }
      if (res.status === 429) return { ok: false, why: 'flood' };
      if (!res.ok || typeof a?.ok !== 'boolean') return { ok: false, why: 'unavailable' };
      onEvent('chat_name_saved', { result: a.ok ? (a.name === null ? 'cleared' : 'set') : a.why });
      return a.ok ? { ok: true, name: typeof a.name === 'string' ? a.name : null } : { ok: false, why: String(a.why), ...(Number.isFinite(a.until) && { until: a.until }) };
    },
    /** Delete account: the worker forgets it; this device keeps its copy (a new account would take all of it). */
    async remove() {
      if (!state) return false;
      let res;
      try { res = await call('/account/delete', ''); } catch { return false; }
      if (!res.ok && res.status !== 401) return false;
      signedOut('deleted');
      onEvent('account_deleted', {});
      return true;
    },
    /**
     * A synced setting changed on this device (the page's store says so): when, and off to the account soon. Never
     * earlier than the time this device has for it from the account: a change made after a newer one from a device
     * whose clock runs ahead still wins.
     */
    changed(key) {
      if (applying || !keys.has(key)) return;
      const had = ats()[key];
      store.set('settings-at', { ...ats(), [key]: Math.max(now(), Number.isFinite(had) ? had + 1 : 0) });
      if (!state) return;
      dirty = true;
      timers.clearTimeout(soon);
      soon = timers.setTimeout(() => sync(), SOON_MS);
    },
    sync,
  };
}
